<template>
	<div class="grid grid-cols-2 pt-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 gap-3 md:gap-4 mb-8">
		<AbsoluteInternalLink
			v-for="item in sortedCategories"
			:key="item.categoryId"
			:to="routeGenerator(item)"
			class="
				rem:min-h-[50px]
				md:rem:min-h-[72px]
				shadow-gray-lg
				focus-outline
				px-4
				py-3
				xs:py-4
				flex
				items-center
				gap-2
				font-semibold
				text-body-primary
				rounded-2.5xl
				rem:leading-[19px]
				xs:rem:max-h-[72px]
				typo-body-m
			"
		>
			<NuxtImg
				v-if="categoriesMapFromStack[item.categoryId] && item.categoryId"
				:src="categoriesMapFromStack[item.categoryId]?.icon.filename"
				alt=""
				width="32px"
				height="32px"
				provider="storyblok"
			/>
			{{ item.categoryName }}
		</AbsoluteInternalLink>
	</div>
	{{ categoryStack?.content.CategoryList }}
</template>

<script setup lang="ts">
	import type {RouteLocationRaw} from 'vue-router';
	import type {CategoryStackListItemStoryblok, CategoryStackListStoryblok} from '~/storyblok/types';
	import type {Gsoa_ProductCatalog_Eshop_ProductCategoryEshop as ProductCategory} from '~/openapi';

	interface Props {
		categories: ProductCategory[]
		routeGenerator: (cat: ProductCategory) => RouteLocationRaw
		rootCategoryId?: string
		isPending?: boolean
		isProductCatalog?: boolean
	}

	const props = defineProps<Props>();

	const fullSlug = '_globus-cz/komponenty/seznam-kategorii';
	const {data: categoryStack} = await useSingleStoryFetch<CategoryStackListStoryblok>(fullSlug);

	// Selects the correct Storyblok list based on the current context (Action offer vs Product catalog)
	const activeStoryblokList = computed(() => {
		return props.isProductCatalog
			? (categoryStack.value?.content.categoryListProductCatalog || [])
			: (categoryStack.value?.content.categoryList || []);
	});

	// Creates a dictionary to quickly match API categories with Storyblok icons
	const categoriesMapFromStack = computed(() => {
		const map: Record<string, CategoryStackListItemStoryblok> = {};
		activeStoryblokList.value.forEach((category: CategoryStackListItemStoryblok) => {
			map[category.categoryId] = category;
		});
		return map;
	});

	// Sorts the API categories based on the order defined by editors in Storyblok.
	// Unknown categories (missing in SB) are pushed to the end.
	const sortedCategories = computed(() => {
		const orderMap = new Map<string, number>(
			activeStoryblokList.value.map((item: CategoryStackListItemStoryblok, index: number) => [
				item.categoryId || '',
				index,
			]),
		);

		return [...props.categories].sort((a, b) => {
			const idA = a.categoryId ?? '';
			const idB = b.categoryId ?? '';

			const indexA = orderMap.has(idA) ? orderMap.get(idA)! : Infinity;
			const indexB = orderMap.has(idB) ? orderMap.get(idB)! : Infinity;

			return indexA - indexB;
		});
	});
</script>
